/**
 * Module dependencies.
 */

import { Text, baseFontSize, clampCalc } from 'src/components/core/text';
import { media } from 'src/styles/media';
import { normalizeNewLine } from 'src/core/utils/string';
import {
  useChronologyImages,
  useChronologyItems
} from 'src/core/content-config/saramago-chronology';

import Container from 'src/components/core/layout/container';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Image from 'src/components/core/image';
import RawHtml from 'src/components/core/raw-html';
import map from 'lodash/map';
import slice from 'lodash/slice';
import styled from 'styled-components';
import useBreakpoint from 'src/hooks/use-breakpoint';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Section` styled component.
 */

const Section = styled.section`
  color: var(--color-grey-700);
  padding-bottom: 5rem;

  ${media.min.md`
    padding-bottom: 8.125rem;
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min.md`
    display: grid;
    grid-template-areas:
      '.      chronologyTop    chronologyTop    .'
      'images images           images           images'
      '.      chronologyMiddle chronologyMiddle .'
      '.      singleImage      singleImage      .'
      '.      chronologyBottom chronologyBottom .'
      '.      .                description      .';
    grid-template-columns: repeat(2, 1fr) 3fr 1fr;
  `}
`;

/**
 * `ChronologyItemTopWrapper` styled component.
 */

const ChronologyItemTopWrapper = styled.div`
  ${media.min.md`
    grid-area: chronologyTop;
  `}
`;

/**
 * `ChronologyItemMiddleWrapper` styled component.
 */

const ChronologyItemMiddleWrapper = styled.div`
  ${media.min.md`
    grid-area: chronologyMiddle;
  `}
`;

/**
 * `ChronologyItemBottomWrapper` styled component.
 */

const ChronologyItemBottomWrapper = styled.div`
  ${media.min.md`
    grid-area: chronologyBottom;
  `}
`;

/**
 * `ChronologyItem` styled component.
 */

const ChronologyItem = styled.div`
  position: relative;

  ${media.min.md`
    display: grid;
    grid-template-columns: 2fr 6fr;
    margin-bottom: 3.75rem;
  `}
`;

/**
 * `Year` styled component.
 */

const Year = styled(Text)`
  color: var(--color-dark);

  ${media.max.md`
    font-size: 2.8125rem;
    line-height: 3.75rem;
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled.p`
  font-family: var(--font-futura-pt);
  font-size: clamp(
    ${16 / baseFontSize}rem,
    ${clampCalc(16, 19)},
    ${19 / baseFontSize}rem
  );
  letter-spacing: clamp(
    ${0.4 / baseFontSize}rem,
    ${clampCalc(0.4, 0.47)},
    ${0.47 / baseFontSize}rem
  );
  line-height: clamp(
    ${20 / baseFontSize}rem,
    ${clampCalc(20, 30)},
    ${30 / baseFontSize}rem
  );
  margin-bottom: 2.625rem;

  ${media.min.md`
    margin-bottom: 1.25rem;
  `}
`;

/**
 * `Quote` styled component.
 */

const Quote = styled(Text)`
  color: var(--color-dark);

  ${media.max.md`
    text-align: center;
  `}

  &::before {
    background-color: var(--color-grey-400);
    bottom: -1.875rem;
    content: '';
    left: -30%;
    position: absolute;
    top: -0.5rem;
    width: 1px;

    ${media.max.md`
      display: none;
    `}
  }
`;

/**
 * `ImagesGrid` styled component.
 */

const ImagesGrid = styled.div`
  display: flex;
  flex-direction: column;
  grid-row-gap: 1.875rem;
  margin-bottom: 1.875rem;

  ${media.min.md`
    display: grid;
    grid-area: images;
    grid-column-gap: calc(((100vw/12)*1.6) - 48px);
    grid-template-columns: repeat(3, 1fr);
    margin-bottom: 7.5rem;
  `}

  ${media.min.xxl`
    grid-column-gap: calc((var(--container-max-width)/12)*1.6);
  `}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  overflow: hidden;
  padding-bottom: 143.8%;
  position: relative;
`;

/**
 * `ImageBig` styled component.
 */

const ImageBig = styled.div`
  margin-bottom: 2.5rem;

  ${media.min.md`
    grid-area: singleImage;
    margin-bottom: 5.5rem;
  `}
`;

/**
 * `ImageBigWrapper` styled component.
 */

const ImageBigWrapper = styled.div`
  overflow: hidden;
  padding-bottom: 59.2%;
  position: relative;
`;

/**
 * `Legend` styled component.
 */

const Legend = styled(RawHtml)`
  display: flex;
  font-family: var(--font-futura-pt);
  font-size: 0.8125rem;
  justify-content: flex-end;
  letter-spacing: 0.33px;
  line-height: clamp(
    ${15 / baseFontSize}rem,
    ${clampCalc(15, 30)},
    ${30 / baseFontSize}rem
  );
  margin-left: auto;
  padding-top: 0.75rem;
  text-align: right;

  ${media.max.md`
    flex-direction: column;
  `}

  span {
    ${media.min.md`
      margin-left: 0.25rem;
    `}
  }
`;

/**
 * `SectionDescription` styled component.
 */

const SectionDescription = styled(RawHtml)`
  font-family: var(--font-futura-pt);
  font-size: clamp(
    ${16 / baseFontSize}rem,
    ${clampCalc(16, 19)},
    ${19 / baseFontSize}rem
  );
  letter-spacing: clamp(
    ${0.4 / baseFontSize}rem,
    ${clampCalc(0.4, 0.47)},
    ${0.47 / baseFontSize}rem
  );
  line-height: clamp(
    ${20 / baseFontSize}rem,
    ${clampCalc(20, 30)},
    ${30 / baseFontSize}rem
  );
`;

/**
 * Export `BioChronologySection` component.
 */

const BioChronologySection = () => {
  const chronologyItems = useChronologyItems();
  const chronologyImageItems = useChronologyImages();
  const { translate } = useTranslate();
  const isMobile = useBreakpoint('md', 'max');

  return (
    <Section>
      <Container>
        <Grid>
          <ChronologyItemTopWrapper>
            {map(
              slice(chronologyItems, 0, 1),
              ({ description, quote, year }, index: number) => (
                <ChronologyItem key={index}>
                  <FadeInUpAnimation
                    options={{
                      distance: '100%',
                      transitionTime: 1
                    }}
                  >
                    <Year
                      as={'h3'}
                      variant={'titleXS'}
                    >
                      {year}
                    </Year>
                  </FadeInUpAnimation>

                  <div>
                    <FadeInUpAnimation
                      options={{
                        distance: '100%',
                        transitionDelay: isMobile ? 0 : 0.2,
                        transitionTime: 1
                      }}
                    >
                      <Description>{description}</Description>
                    </FadeInUpAnimation>

                    {quote && (
                      <FadeInUpAnimation
                        options={{
                          distance: '100%',
                          transitionDelay: isMobile ? 0 : 0.2,
                          transitionTime: 1
                        }}
                      >
                        <Quote
                          as={'p'}
                          variant={'quoteBlock'}
                        >
                          {quote}
                        </Quote>
                      </FadeInUpAnimation>
                    )}
                  </div>
                </ChronologyItem>
              )
            )}
          </ChronologyItemTopWrapper>

          <ImagesGrid>
            {slice(chronologyImageItems, 0, 3).map(
              ({ imageTitle, imageUrl }, index: number) => (
                <FadeInUpAnimation
                  key={index}
                  options={{
                    distance: '25%',
                    transitionDelay: 0.2 * index,
                    transitionTime: 1
                  }}
                >
                  <ImageWrapper>
                    <Image
                      alt={imageTitle}
                      layout={'fill'}
                      objectFit={'cover'}
                      src={imageUrl}
                    />
                  </ImageWrapper>
                </FadeInUpAnimation>
              )
            )}
          </ImagesGrid>

          <ChronologyItemMiddleWrapper>
            {map(
              slice(chronologyItems, 1, 4),
              ({ description, quote, year }, index: number) => (
                <ChronologyItem key={index}>
                  <FadeInUpAnimation
                    options={{
                      distance: '100%',
                      transitionTime: 1
                    }}
                  >
                    <Year
                      as={'h3'}
                      variant={'titleXS'}
                    >
                      {year}
                    </Year>
                  </FadeInUpAnimation>

                  <div>
                    <FadeInUpAnimation
                      options={{
                        distance: '100%',
                        transitionDelay: isMobile ? 0 : 0.2,
                        transitionTime: 1
                      }}
                    >
                      <Description>{description}</Description>
                    </FadeInUpAnimation>

                    {quote && (
                      <FadeInUpAnimation
                        options={{
                          distance: '100%',
                          transitionDelay: isMobile ? 0 : 0.2,
                          transitionTime: 1
                        }}
                      >
                        <Quote variant={'quoteBlock'}>{quote}</Quote>
                      </FadeInUpAnimation>
                    )}
                  </div>
                </ChronologyItem>
              )
            )}
          </ChronologyItemMiddleWrapper>

          <ImageBig>
            {slice(chronologyImageItems, 3, chronologyImageItems.length).map(
              ({ imageTitle, imageUrl, legend }, index: number) => (
                <FadeInUpAnimation
                  key={index}
                  options={{
                    distance: '25%',
                    transitionTime: 1
                  }}
                >
                  <ImageBigWrapper>
                    <Image
                      alt={imageTitle}
                      layout={'fill'}
                      objectFit={'cover'}
                      src={imageUrl}
                    />
                  </ImageBigWrapper>

                  {legend && <Legend>{normalizeNewLine(legend)}</Legend>}
                </FadeInUpAnimation>
              )
            )}
          </ImageBig>

          <ChronologyItemBottomWrapper>
            {map(
              slice(chronologyItems, 4, chronologyItems.length),
              ({ description, quote, year }, index: number) => (
                <ChronologyItem key={index}>
                  <FadeInUpAnimation
                    options={{
                      distance: '100%',
                      transitionTime: 1
                    }}
                  >
                    <Year
                      as={'h3'}
                      variant={'titleXS'}
                    >
                      {year}
                    </Year>
                  </FadeInUpAnimation>

                  <div>
                    <FadeInUpAnimation
                      options={{
                        distance: '100%',
                        transitionDelay: isMobile ? 0 : 0.2,
                        transitionTime: 1
                      }}
                    >
                      <Description>{description}</Description>
                    </FadeInUpAnimation>

                    {quote && (
                      <FadeInUpAnimation
                        options={{
                          distance: '100%',
                          transitionDelay: isMobile ? 0 : 0.2,
                          transitionTime: 1
                        }}
                      >
                        <Quote variant={'quoteBlock'}>{quote}</Quote>
                      </FadeInUpAnimation>
                    )}
                  </div>
                </ChronologyItem>
              )
            )}
          </ChronologyItemBottomWrapper>

          <div style={{ gridArea: 'description ' }}>
            <FadeInUpAnimation
              options={{
                distance: '25%',
                transitionTime: 1
              }}
            >
              <SectionDescription>
                {normalizeNewLine(
                  translate('biography-saramago:chronologySection.description')
                )}
              </SectionDescription>
            </FadeInUpAnimation>
          </div>
        </Grid>
      </Container>
    </Section>
  );
};

/**
 * Export `BioChronologySection` component.
 */

export default BioChronologySection;
